import {
  documentToReactComponents,
  Options as WeaklyTypedRichTextOptions,
} from "@contentful/rich-text-react-renderer"
import {
  BLOCKS,
  INLINES,
  Block,
  Inline,
  Document,
} from "@contentful/rich-text-types"
import type { Asset, Entry } from "contentful"
import Image from "next/image"
import Link from "next/link"
import React, { ReactNode } from "react"

import { runIfEnv } from "@/helpers/utils"

/**
 * Define contentful blocks with possible block types
 */
export type ContentfulBlock<T extends BLOCKS = BLOCKS> = {
  nodeType: T
} & Block

/**
 * Define contentful inline elements with possible inline types
 */
export type ContentfulInline<T extends INLINES = INLINES> = {
  nodeType: T
} & Inline

/**
 * Callback for rendering blocks
 */
export type BlockNodeRenderer<T extends BLOCKS = BLOCKS> = (
  node: ContentfulBlock<T>,
  children: ReactNode,
) => ReactNode

/**
 * Callback for rendering inline entries
 */
export type InlineNodeRenderer<T extends INLINES = INLINES> = (
  node: ContentfulInline<T>,
  children: ReactNode,
) => ReactNode

/**
 * Combine the rendering options for blocks and inline
 */
export type RenderNode = { [B in BLOCKS]?: BlockNodeRenderer<B> } & {
  [I in INLINES]?: InlineNodeRenderer<I>
}

/**
 * Type for handling the mark elements in the richText
 */
type Mark = "bold" | "italic" | "underline" | "code"

/**
 * Callback to handle the rendering of marks
 */
export type RenderMark = {
  [M in Mark]?: (text: ReactNode) => ReactNode
}

/**
 * The renderText callback is a function that has a single string argument and returns a React node
 * Each text is evaluated individually by this callback.
 * A possible use case for this is to replace instances of the \n produced by the SHIFT + ENTER with
 * <br /> react elements
 */
export type RenderText = (text: string) => ReactNode

/**
 * Options that allow for customization of how rich text is rendered
 * Required to render embedded entries
 * @See https://www.npmjs.com/package/@contentful/rich-text-react-renderer
 */
export type RichTextOptions = {
  /**
   * Node renderers
   */
  renderNode?: RenderNode
  /**
   * Mark renderers
   */
  renderMark?: RenderMark
  /**
   * Text renderer
   */
  renderText?: RenderText
}

/**
 * Define the types for the component which accepts the document that needs to be rendered
 */
export type ContentfulRichTextProps = {
  document: Document | null
  options?: RichTextOptions
  children?: never
}

export const combineOptions = (
  root: RichTextOptions,
  overrides: RichTextOptions,
): RichTextOptions => ({
  renderNode: {
    ...root.renderNode,
    ...overrides.renderNode,
  },
  renderMark: {
    ...root.renderMark,
    ...overrides.renderMark,
  },
  renderText: overrides.renderText || root.renderText,
})

const DEFAULT_OPTIONS: RichTextOptions = {
  renderNode: {
    [BLOCKS.EMBEDDED_ENTRY]: (node: Block): ReactNode => {
      const entry = node?.data?.target as Entry

      if (!entry) {
        runIfEnv("development", () => {
          console.warn(
            "ContentfulRichText: document prop is missing or invalid.",
          )
        })
      }
      return null
    },
    [INLINES.EMBEDDED_ENTRY]: (node: Inline): ReactNode => {
      const entry = node?.data?.target as Entry

      if (!entry) {
        runIfEnv("development", () => {
          console.warn(
            "ContentfulRichText: document prop is missing or invalid.",
          )
        })
      }

      return null
    },
    [INLINES.ENTRY_HYPERLINK]: (node: Inline): ReactNode => {
      const entry = node?.data?.target as Entry

      if (!entry) {
        runIfEnv("development", () => {
          console.warn(
            "ContentfulRichText: document prop is missing or invalid.",
          )
        })
      }

      return null
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = node?.data?.target as
        | Asset<"WITHOUT_LINK_RESOLUTION">
        | undefined

      if (!asset?.fields?.file) {
        runIfEnv("development", () => {
          console.warn(
            "ContentfulRichText: document prop is missing or invalid.",
          )
        })
        return null
      }

      const { file, description } = asset.fields

      if (!file.url || !file.details?.image?.width) {
        runIfEnv("development", () => {
          console.warn(
            "ContentfulRichText: document prop is missing or invalid.",
          )
        })
        return null
      }

      switch (file.contentType) {
        case "image/apng":
        case "image/avif":
        case "image/gif":
        case "image/jpg":
        case "image/jpeg":
        case "image/jfif":
        case "image/pjpeg":
        case "image/pjp":
        case "image/png":
        case "image/svg":
        case "image/webp":
          return (
            <Image
              src={file.url}
              width={file.details.image.width}
              height={file.details.image.width}
              alt={description}
              itemProp="image"
              className="my-6"
            />
          )
        default:
          return null
      }
    },
    [INLINES.HYPERLINK]: (
      node: ContentfulInline<INLINES.HYPERLINK>,
      children: ReactNode,
    ) => (
      <Link
        className="text-blue-60"
        href={node?.data?.uri as string}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </Link>
    ),
  },
  renderText: (text) =>
    text
      .split("\n")
      .flatMap((segment, i) =>
        i > 0 ? [<br key={segment} />, segment] : segment,
      ),
} as const

/**
 * Rendered rich text from a json Document block
 *
 * Note: this component only supports rendering rich text at this time, and will need added support
 * for rendering media/assets/references later on
 *
 * @param document
 * @param options
 * @constructor
 */
export const ContentfulRichText: React.FC<ContentfulRichTextProps> = ({
  document = null,
  options = {},
}) => {
  if (!document) {
    runIfEnv("development", () => {
      console.warn("ContentfulRichText: document prop is missing or invalid.")
    })
    return null
  }

  // Safely exit if there are issues accessing any of the rich text API data
  try {
    return (
      <>
        {documentToReactComponents(
          document,
          combineOptions(
            DEFAULT_OPTIONS,
            options,
          ) as WeaklyTypedRichTextOptions,
        )}
      </>
    )
  } catch (error) {
    runIfEnv("development", () => {
      console.error("Error rendering ContentfulRichText:", error)
    })

    return null
  }
}

ContentfulRichText.displayName = "ContentfulRichText"
